import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getUnreadNotificationCount, getNotifications } from '../../api/notifications';

// Async thunks
export const fetchUnreadCount = createAsyncThunk(
    'notifications/fetchUnreadCount',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getUnreadNotificationCount();
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchNotifications = createAsyncThunk(
    'notifications/fetchAll',
    async ({ page = 1, limit = 20 }, { rejectWithValue }) => {
        try {
            const skip = (page - 1) * limit;
            const response = await getNotifications({ skip, limit });
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

const initialState = {
    // Уведомления
    notifications: [],
    unreadCount: 0,
    
    // Состояния загрузки
    loading: {
        unreadCount: false,
        notifications: false
    },
    
    // Ошибки
    errors: {
        unreadCount: null,
        notifications: null
    },
    
    // Пагинация
    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        hasMore: true
    },
    
    // Фильтры
    filters: {
        isRead: null, // null = все, true = прочитанные, false = непрочитанные
        type: null, // тип уведомления
        dateFrom: null,
        dateTo: null
    }
};

const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        // Установка уведомления как прочитанного
        markAsRead: (state, action) => {
            const notificationId = action.payload;
            const notification = state.notifications.find(n => n.id === notificationId);
            if (notification && !notification.is_read) {
                notification.is_read = true;
                state.unreadCount = Math.max(0, state.unreadCount - 1);
            }
        },
        
        // Установка всех уведомлений как прочитанных
        markAllAsRead: (state) => {
            state.notifications.forEach(notification => {
                notification.is_read = true;
            });
            state.unreadCount = 0;
        },
        
        // Удаление уведомления
        removeNotification: (state, action) => {
            const notificationId = action.payload;
            const notification = state.notifications.find(n => n.id === notificationId);
            if (notification && !notification.is_read) {
                state.unreadCount = Math.max(0, state.unreadCount - 1);
            }
            state.notifications = state.notifications.filter(n => n.id !== notificationId);
        },
        
        // Добавление нового уведомления
        addNotification: (state, action) => {
            const notification = action.payload;
            state.notifications.unshift(notification);
            if (!notification.is_read) {
                state.unreadCount += 1;
            }
        },
        
        // Установка фильтров
        setFilters: (state, action) => {
            state.filters = { ...state.filters, ...action.payload };
            state.pagination.page = 1;
        },
        
        // Сброс фильтров
        resetFilters: (state) => {
            state.filters = {
                isRead: null,
                type: null,
                dateFrom: null,
                dateTo: null
            };
            state.pagination.page = 1;
        },
        
        // Установка страницы
        setPage: (state, action) => {
            state.pagination.page = action.payload;
        },
        
        // Очистка данных
        clearNotifications: (state) => {
            state.notifications = [];
            state.pagination.page = 1;
            state.pagination.total = 0;
            state.pagination.hasMore = true;
        },
        
        // Сброс ошибок
        clearErrors: (state) => {
            state.errors = {
                unreadCount: null,
                notifications: null
            };
        }
    },
    extraReducers: (builder) => {
        // Unread count
        builder
            .addCase(fetchUnreadCount.pending, (state) => {
                state.loading.unreadCount = true;
                state.errors.unreadCount = null;
            })
            .addCase(fetchUnreadCount.fulfilled, (state, action) => {
                state.loading.unreadCount = false;
                state.unreadCount = action.payload.unread_count || 0;
            })
            .addCase(fetchUnreadCount.rejected, (state, action) => {
                state.loading.unreadCount = false;
                state.errors.unreadCount = action.payload;
            });
        
        // Notifications
        builder
            .addCase(fetchNotifications.pending, (state) => {
                state.loading.notifications = true;
                state.errors.notifications = null;
            })
            .addCase(fetchNotifications.fulfilled, (state, action) => {
                state.loading.notifications = false;
                state.notifications = action.payload.notifications || [];
                state.pagination.total = action.payload.total || 0;
                state.pagination.hasMore = (action.payload.notifications || []).length >= state.pagination.limit;
            })
            .addCase(fetchNotifications.rejected, (state, action) => {
                state.loading.notifications = false;
                state.errors.notifications = action.payload;
            });
    }
});

export const {
    markAsRead,
    markAllAsRead,
    removeNotification,
    addNotification,
    setFilters,
    resetFilters,
    setPage,
    clearNotifications,
    clearErrors
} = notificationsSlice.actions;

// Selectors
export const selectNotifications = (state) => state.notifications.notifications;
export const selectUnreadCount = (state) => state.notifications.unreadCount;
export const selectNotificationsLoading = (state) => state.notifications.loading;
export const selectNotificationsErrors = (state) => state.notifications.errors;
export const selectNotificationsPagination = (state) => state.notifications.pagination;
export const selectNotificationsFilters = (state) => state.notifications.filters;

export default notificationsSlice.reducer;
